import { useId } from "react";

// Warna gradien ikut peranan (padanan ROLE_AI.grad dalam aiApi.js)
const ROLE_COLORS = {
  superadmin: ["#d946ef", "#f43f5e"],
  branch_manager: ["#6366f1", "#0ea5e9"],
  admin: ["#14b8a6", "#10b981"],
  guru: ["#f97316", "#f59e0b"],
  ibu_bapa: ["#ec4899", "#fb923c"],
};

/**
 * Ikon AI Assistant berbentuk kepala Mickey (2 telinga + kepala).
 * variant: "face" (muka comel beranimasi), "close" (ikon X), "silhouette" (bentuk sahaja - untuk kesan ping)
 * mood   : "idle" (biasa), "thinking" (berfikir - mata pandang atas, gelembung fikiran), "happy" (gembira - mata ^ ^, senyum lebar, hati),
 *          "sad" (sedih & perlu bantuan - air mata, telinga layu, tanda !), "wave" (melambai - sapaan)
 */
export default function MickeyAIIcon({ role = "ibu_bapa", variant = "face", mood = "idle", className = "", outline = true, animated = true }) {
  const uid = useId().replace(/:/g, "");
  const [c1, c2] = ROLE_COLORS[role] || ROLE_COLORS.ibu_bapa;
  const g = `mk-grad-${uid}`;
  const ge = `mk-ear-${uid}`;
  const shine = `mk-shine-${uid}`;
  const eye = `mk-eye-${uid}`;

  if (variant === "silhouette") {
    return (
      <svg viewBox="0 0 100 100" className={className} aria-hidden="true">
        <defs>
          <linearGradient id={g} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
          </linearGradient>
        </defs>
        <g fill={`url(#${g})`}>
          <circle cx="23" cy="25" r="18" /><circle cx="77" cy="25" r="18" /><circle cx="50" cy="60" r="35" />
        </g>
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 100 100" className={`mk-ai mk-mood-${variant === "close" ? "idle" : mood} ${animated ? "mk-animated" : ""} ${className}`} aria-hidden="true"
      style={{ overflow: "visible" }} data-mood={variant === "close" ? "idle" : mood}>
      <g className="mk-body">
      <defs>
        <linearGradient id={g} x1="0.1" y1="0" x2="0.9" y2="1">
          <stop offset="0%" stopColor={c1} /><stop offset="100%" stopColor={c2} />
        </linearGradient>
        <linearGradient id={ge} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={c2} /><stop offset="100%" stopColor={c1} />
        </linearGradient>
        <linearGradient id={eye} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2b2140" /><stop offset="100%" stopColor="#5b4785" />
        </linearGradient>
        <radialGradient id={shine} cx="0.35" cy="0.25" r="0.6">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Garis luar putih (siluet bersatu tanpa garisan sambungan) */}
      {outline && (
        <g fill="#ffffff">
          <circle className="mk-ear-l" cx="23" cy="25" r="21.5" />
          <circle className="mk-ear-r" cx="77" cy="25" r="21.5" />
          <circle cx="50" cy="60" r="38.5" />
        </g>
      )}

      {/* Telinga */}
      <circle className="mk-ear-l" cx="23" cy="25" r="18" fill={`url(#${ge})`} />
      <circle className="mk-ear-r" cx="77" cy="25" r="18" fill={`url(#${ge})`} />
      {/* Kepala */}
      <circle cx="50" cy="60" r="35" fill={`url(#${g})`} />
      <circle cx="50" cy="60" r="35" fill={`url(#${shine})`} />

      {variant === "close" ? (
        <g stroke="#ffffff" strokeWidth="7" strokeLinecap="round">
          <line x1="37" y1="47" x2="63" y2="73" /><line x1="63" y1="47" x2="37" y2="73" />
        </g>
      ) : (
        <>
          {/* Topeng muka */}
          <path d="M50 40 C40 40 33 47 35 56 C27 58 24 69 31 77 C38 87 62 87 69 77 C76 69 73 58 65 56 C67 47 60 40 50 40 Z"
            fill="#fff8f0" />
          {mood === "thinking" ? (
            <>
              {/* Kening berfikir (sebelah terangkat) */}
              <g stroke="#2b2140" strokeWidth="1.8" fill="none" strokeLinecap="round">
                <path d="M36.5 47.5 Q41 45.5 45.5 47.5" />
                <path d="M54.5 46 Q59 42.5 63.5 45" />
              </g>
              {/* Mata pandang ke atas-kanan */}
              <g className="mk-eyes mk-eyes-look">
                <ellipse cx="41.5" cy="57" rx="5.6" ry="7" fill={`url(#${eye})`} />
                <ellipse cx="58.5" cy="57" rx="5.6" ry="7" fill={`url(#${eye})`} />
                <circle cx="44" cy="52.8" r="2.5" fill="#ffffff" />
                <circle cx="61" cy="52.8" r="2.5" fill="#ffffff" />
              </g>
            </>
          ) : mood === "happy" || mood === "wave" ? (
            <g stroke="#2b2140" strokeWidth="3" fill="none" strokeLinecap="round">
              <path d="M36 58.5 Q41.5 50.5 47 58.5" />
              <path d="M53 58.5 Q58.5 50.5 64 58.5" />
            </g>
          ) : mood === "sad" ? (
            <>
              {/* Kening risau */}
              <g stroke="#2b2140" strokeWidth="1.8" fill="none" strokeLinecap="round">
                <path d="M36.5 48.5 Q41 48.5 45.5 45.5" />
                <path d="M54.5 45.5 Q59 48.5 63.5 48.5" />
              </g>
              {/* Mata berair */}
              <g className="mk-eyes">
                <ellipse cx="41.5" cy="58" rx="5.4" ry="6.4" fill={`url(#${eye})`} />
                <ellipse cx="58.5" cy="58" rx="5.4" ry="6.4" fill={`url(#${eye})`} />
                <circle cx="43" cy="55.6" r="2.2" fill="#ffffff" />
                <circle cx="60" cy="55.6" r="2.2" fill="#ffffff" />
                <circle cx="40" cy="60.6" r="1.3" fill="#ffffff" opacity="0.9" />
                <circle cx="57" cy="60.6" r="1.3" fill="#ffffff" opacity="0.9" />
                <path d="M36.4 62.5 Q41.5 65.6 46.6 62.5" stroke="#93c5fd" strokeWidth="1.4" fill="none" opacity="0.9" />
                <path d="M53.4 62.5 Q58.5 65.6 63.6 62.5" stroke="#93c5fd" strokeWidth="1.4" fill="none" opacity="0.9" />
              </g>
            </>
          ) : (
            <>
              {/* Mata besar berkilau (biasa) */}
              <g className="mk-eyes-open">
                <g className="mk-eyes">
                  <ellipse cx="41.5" cy="57" rx="5.6" ry="7" fill={`url(#${eye})`} />
                  <ellipse cx="58.5" cy="57" rx="5.6" ry="7" fill={`url(#${eye})`} />
                  <circle cx="43.4" cy="54.1" r="2.4" fill="#ffffff" />
                  <circle cx="60.4" cy="54.1" r="2.4" fill="#ffffff" />
                  <circle cx="39.8" cy="59.9" r="1.1" fill="#ffffff" opacity="0.9" />
                  <circle cx="56.8" cy="59.9" r="1.1" fill="#ffffff" opacity="0.9" />
                </g>
              </g>
              {/* Mata gembira ^ ^ (bila hover) */}
              <g className="mk-eyes-happy" stroke="#2b2140" strokeWidth="2.8" fill="none" strokeLinecap="round">
                <path d="M36.5 58.5 Q41.5 51.5 46.5 58.5" />
                <path d="M53.5 58.5 Q58.5 51.5 63.5 58.5" />
              </g>
            </>
          )}
          {/* Pipi merah jambu + kilau */}
          <ellipse cx="33.5" cy="67" rx="5.2" ry="3.3" fill="#fb7185" opacity={mood === "happy" ? 0.85 : 0.55} />
          <ellipse cx="66.5" cy="67" rx="5.2" ry="3.3" fill="#fb7185" opacity={mood === "happy" ? 0.85 : 0.55} />
          <g stroke="#ffffff" strokeWidth="0.9" strokeLinecap="round" opacity="0.9">
            <line x1="31.5" y1="68.2" x2="32.6" y2="66" /><line x1="34" y1="68.2" x2="35.1" y2="66" />
            <line x1="64.9" y1="68.2" x2="66" y2="66" /><line x1="67.4" y1="68.2" x2="68.5" y2="66" />
          </g>
          {/* Hidung kecil */}
          <ellipse cx="50" cy="64.6" rx="2.7" ry="2" fill="#2b2140" />
          <circle cx="49.1" cy="64" r="0.7" fill="#ffffff" />
          {mood === "thinking" ? (
            /* Mulut "hmm" */
            <path className="mk-hmm" d="M45 71 Q47.5 69 50 71 T55 70.5" stroke="#2b2140" strokeWidth="2.2" fill="none" strokeLinecap="round" />
          ) : mood === "sad" ? (
            /* Mulut sedih */
            <path className="mk-frown" d="M44.5 73.5 Q50 67.5 55.5 73.5" stroke="#2b2140" strokeWidth="2.6" fill="none" strokeLinecap="round" />
          ) : mood === "happy" || mood === "wave" ? (
            /* Senyum lebar */
            <g className="mk-mouth">
              <path d="M41.5 68 Q50 81 58.5 68 Q50 70.5 41.5 68 Z" fill="#7f1d1d" />
              <path d="M45.5 73.5 Q50 78.2 54.5 73.5 Q50 71.8 45.5 73.5 Z" fill="#fb7185" />
            </g>
          ) : (
            /* Mulut senyum terbuka dengan lidah */
            <g className="mk-mouth">
              <path d="M44.8 68.4 Q50 76.5 55.2 68.4 Q50 70.2 44.8 68.4 Z" fill="#7f1d1d" />
              <path d="M47.3 72 Q50 75 52.7 72 Q50 70.9 47.3 72 Z" fill="#fb7185" />
            </g>
          )}
          {/* Reben comel */}
          <g className="mk-bow" stroke="#ffffff" strokeWidth="1.6" strokeLinejoin="round">
            <path d="M50 28 C44 19 35 21 36.5 28 C35 35 44 37 50 28 Z" fill="#fde047" />
            <path d="M50 28 C56 19 65 21 63.5 28 C65 35 56 37 50 28 Z" fill="#fde047" />
            <circle cx="50" cy="28" r="3.8" fill="#facc15" />
            <g fill="#ffffff" stroke="none">
              <circle cx="41" cy="26" r="1.2" /><circle cx="43" cy="31" r="1" /><circle cx="59" cy="26" r="1.2" /><circle cx="57" cy="31" r="1" />
            </g>
          </g>
          {mood === "thinking" ? (
            /* Gelembung fikiran */
            <g className="mk-thought" fill="#ffffff" stroke={c1} strokeWidth="1.4">
              <circle className="mk-t1" cx="80" cy="44" r="2.6" />
              <circle className="mk-t2" cx="86.5" cy="35" r="3.8" />
              <g className="mk-t3">
                <ellipse cx="94" cy="20" rx="11" ry="8.5" />
                <g fill={c1} stroke="none">
                  <circle className="mk-d1" cx="89.5" cy="20" r="1.5" />
                  <circle className="mk-d2" cx="94" cy="20" r="1.5" />
                  <circle className="mk-d3" cx="98.5" cy="20" r="1.5" />
                </g>
              </g>
            </g>
          ) : mood === "sad" ? (
            /* Air mata + tanda perlu bantuan */
            <>
              <path className="mk-tear" d="M37 64 C35 68 34.5 70 37 71.5 C39.5 70 39 68 37 64 Z" fill="#60a5fa" stroke="#ffffff" strokeWidth="0.8" />
              <path className="mk-sweat" d="M70 40 C68.2 43.5 67.8 45.3 70 46.6 C72.2 45.3 71.8 43.5 70 40 Z" fill="#93c5fd" />
              <g className="mk-help">
                <circle cx="90" cy="24" r="9" fill="#ef4444" stroke="#ffffff" strokeWidth="2" />
                <rect x="88.6" y="18.5" width="2.8" height="7.5" rx="1.4" fill="#ffffff" />
                <circle cx="90" cy="29" r="1.6" fill="#ffffff" />
              </g>
            </>
          ) : mood === "wave" ? (
            /* Tangan bersarung putih melambai */
            <g className="mk-hand">
              <rect x="78" y="80" width="11" height="6" rx="3" fill="#ffffff" stroke="#2b2140" strokeWidth="1.4" transform="rotate(-25 83.5 83)" />
              <path d="M80 76 C78 70 80 64 85 63 C86 59 90 58.5 91.5 61.5 C94 60 97 62 96.5 65 C99 65.5 100 69 98 71.5 C97 77 91 81 85 80.5 C82.5 80.2 81 78.5 80 76 Z"
                fill="#ffffff" stroke="#2b2140" strokeWidth="1.6" strokeLinejoin="round" />
              <g stroke="#2b2140" strokeWidth="1.1" strokeLinecap="round" fill="none" opacity="0.7">
                <path d="M88 66 L86.5 71" /><path d="M92.5 66.5 L90.5 72" />
              </g>
              <path className="mk-wave-line" d="M99 56 Q103 59 101 64" stroke="#fde047" strokeWidth="2" fill="none" strokeLinecap="round" />
            </g>
          ) : mood === "happy" ? (
            /* Hati & bintang terapung */
            <>
              <path className="mk-heart1" d="M86 46 C86 42 91 41 91.5 45 C92 41 97 42 97 46 C97 50 91.5 53 91.5 53 C91.5 53 86 50 86 46 Z" fill="#f43f5e" stroke="#ffffff" strokeWidth="1.2" />
              <path className="mk-heart2" d="M5 50 C5 47 9 46 9.4 49 C9.8 46 13.8 47 13.8 50 C13.8 53 9.4 55.5 9.4 55.5 C9.4 55.5 5 53 5 50 Z" fill="#fb7185" stroke="#ffffff" strokeWidth="1" />
              <g className="mk-spark" fill="#fde047" stroke="#ffffff" strokeWidth="1.2" strokeLinejoin="round">
                <path d="M88 64 L90 69 L95 71 L90 73 L88 78 L86 73 L81 71 L86 69 Z" />
              </g>
            </>
          ) : (
            <>
              {/* Kilauan AI */}
              <g className="mk-spark" fill="#fde047" stroke="#ffffff" strokeWidth="1.2" strokeLinejoin="round">
                <path d="M84 50 L86.5 56.5 L93 59 L86.5 61.5 L84 68 L81.5 61.5 L75 59 L81.5 56.5 Z" />
              </g>
              <g className="mk-spark2" fill="#ffffff">
                <path d="M14 56 L15.4 59.6 L19 61 L15.4 62.4 L14 66 L12.6 62.4 L9 61 L12.6 59.6 Z" />
              </g>
            </>
          )}
        </>
      )}
      </g>
    </svg>
  );
}

export const MICKEY_AI_STYLES = `
  .mk-ai .mk-ear-l, .mk-ai .mk-ear-r { transform-box: fill-box; transform-origin: 50% 90%; }
  .mk-ai .mk-eyes { transform-box: fill-box; transform-origin: 50% 50%; }
  .mk-ai .mk-spark, .mk-ai .mk-spark2 { transform-box: fill-box; transform-origin: 50% 50%; }
  .mk-animated .mk-eyes { animation: mkBlink 4.5s infinite; }
  .mk-animated .mk-spark { animation: mkTwinkle 2.4s ease-in-out infinite; }
  .mk-animated .mk-spark2 { animation: mkTwinkle 2.4s ease-in-out .9s infinite; }
  .mk-ai .mk-bow, .mk-ai .mk-mouth { transform-box: fill-box; transform-origin: 50% 50%; }
  .mk-ai .mk-eyes-open, .mk-ai .mk-eyes-happy { transition: opacity .2s ease; }
  .mk-ai .mk-eyes-happy { opacity: 0; }
  .mk-hover:hover .mk-eyes-open { opacity: 0; }
  .mk-hover:hover .mk-eyes-happy { opacity: 1; }
  .mk-hover:hover .mk-mouth { animation: mkGiggle .5s ease-in-out 2; }
  .mk-animated .mk-bow { animation: mkBow 3.2s ease-in-out infinite; }
  .mk-hover:hover .mk-ear-l { animation: mkEarL .6s ease-in-out; }
  .mk-hover:hover .mk-ear-r { animation: mkEarR .6s ease-in-out; }
  @keyframes mkBlink { 0%, 92%, 100% { transform: scaleY(1); } 95% { transform: scaleY(0.1); } }
  .mk-ai .mk-body { transform-box: view-box; transform-origin: 50% 60%; }
  .mk-ai .mk-t1, .mk-ai .mk-t2, .mk-ai .mk-t3, .mk-ai .mk-heart1, .mk-ai .mk-heart2, .mk-ai .mk-d1, .mk-ai .mk-d2, .mk-ai .mk-d3 { transform-box: fill-box; transform-origin: 50% 50%; }
  .mk-mood-thinking .mk-body { animation: mkTilt 2.6s ease-in-out infinite; }
  .mk-mood-thinking .mk-t1 { animation: mkPop 1.8s ease-in-out infinite; }
  .mk-mood-thinking .mk-t2 { animation: mkPop 1.8s ease-in-out .2s infinite; }
  .mk-mood-thinking .mk-t3 { animation: mkPop 1.8s ease-in-out .4s infinite; }
  .mk-mood-thinking .mk-d1 { animation: mkDot 1.2s ease-in-out infinite; }
  .mk-mood-thinking .mk-d2 { animation: mkDot 1.2s ease-in-out .2s infinite; }
  .mk-mood-thinking .mk-d3 { animation: mkDot 1.2s ease-in-out .4s infinite; }
  .mk-mood-thinking .mk-eyes-look { animation: mkLook 3s ease-in-out infinite; transform-box: fill-box; }
  .mk-mood-happy .mk-body { animation: mkJump .7s cubic-bezier(.3,1.6,.5,1) 3; }
  .mk-mood-happy .mk-ear-l { animation: mkEarL .5s ease-in-out infinite; }
  .mk-mood-happy .mk-ear-r { animation: mkEarR .5s ease-in-out infinite; }
  .mk-mood-happy .mk-heart1 { animation: mkFloat 1.6s ease-out infinite; }
  .mk-mood-happy .mk-heart2 { animation: mkFloat 1.6s ease-out .5s infinite; }
  .mk-mood-happy .mk-mouth { animation: mkGiggle .5s ease-in-out infinite; }
  .mk-ai .mk-tear, .mk-ai .mk-sweat, .mk-ai .mk-help, .mk-ai .mk-hand { transform-box: fill-box; }
  .mk-ai .mk-tear, .mk-ai .mk-sweat { transform-origin: 50% 0%; }
  .mk-ai .mk-help { transform-origin: 50% 50%; }
  .mk-ai .mk-hand { transform-origin: 20% 90%; }
  .mk-mood-sad .mk-ear-l { transform: rotate(-16deg) translate(-1px, 3px); }
  .mk-mood-sad .mk-ear-r { transform: rotate(16deg) translate(1px, 3px); }
  .mk-mood-sad .mk-bow { animation: none; transform: rotate(10deg); }
  .mk-mood-sad .mk-body { animation: mkShiver 2.4s ease-in-out infinite; }
  .mk-mood-sad .mk-tear { animation: mkTear 2s ease-in infinite; }
  .mk-mood-sad .mk-help { animation: mkHelp 1.2s ease-in-out infinite; }
  .mk-mood-wave .mk-hand { animation: mkWave 0.9s ease-in-out infinite; }
  .mk-mood-wave .mk-body { animation: mkTilt 1.8s ease-in-out infinite; }
  .mk-mood-wave .mk-mouth { animation: mkGiggle .6s ease-in-out infinite; }
  @keyframes mkShiver { 0%, 100% { transform: translateX(0); } 20% { transform: translateX(-0.8px); } 40% { transform: translateX(0.8px); } 60% { transform: translateX(0); } }
  @keyframes mkTear { 0% { transform: translateY(-2px) scale(.6); opacity: 0; } 25% { opacity: 1; transform: translateY(0) scale(1); } 100% { transform: translateY(12px) scale(.9); opacity: 0; } }
  @keyframes mkHelp { 0%, 100% { transform: scale(1) rotate(0deg); } 50% { transform: scale(1.15) rotate(-8deg); } }
  @keyframes mkWave { 0%, 100% { transform: rotate(0deg); } 25% { transform: rotate(-22deg); } 75% { transform: rotate(14deg); } }
  @keyframes mkTilt { 0%, 100% { transform: rotate(0deg); } 50% { transform: rotate(-7deg); } }
  @keyframes mkPop { 0%, 100% { transform: scale(.85); opacity: .75; } 50% { transform: scale(1.08); opacity: 1; } }
  @keyframes mkDot { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }
  @keyframes mkLook { 0%, 100% { transform: translate(0, 0); } 50% { transform: translate(1.2px, -1px); } }
  @keyframes mkJump { 0%, 100% { transform: translateY(0) scale(1); } 40% { transform: translateY(-9px) scale(1.04, .97); } 70% { transform: translateY(0) scale(1.05, .94); } }
  @keyframes mkFloat { 0% { transform: translateY(4px) scale(.6); opacity: 0; } 30% { opacity: 1; } 100% { transform: translateY(-16px) scale(1.1); opacity: 0; } }
  @keyframes mkBow { 0%, 100% { transform: rotate(0) scale(1); } 50% { transform: rotate(-6deg) scale(1.06); } }
  @keyframes mkGiggle { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.18, 1.1); } }
  @keyframes mkTwinkle { 0%, 100% { transform: scale(0.75) rotate(0deg); opacity: .75; } 50% { transform: scale(1.15) rotate(20deg); opacity: 1; } }
  @keyframes mkEarL { 0%, 100% { transform: rotate(0); } 30% { transform: rotate(-14deg); } 65% { transform: rotate(8deg); } }
  @keyframes mkEarR { 0%, 100% { transform: rotate(0); } 30% { transform: rotate(14deg); } 65% { transform: rotate(-8deg); } }
`;
